import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Heart } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { SOCIAL } from "@/data/platforms";
import { PlatformIcon } from "@/components/site/PlatformIcon";
import { scrollToId } from "@/components/site/SmoothScroll";

export const Footer = () => {
  const { links } = useSite();
  const navigate = useNavigate();
  const location = useLocation();
  const socialUrls = links?.social || {};
  const socials = SOCIAL.filter((s) => socialUrls[s.name]);

  const anchor = (id) => {
    if (location.pathname === "/") scrollToId(id);
    else navigate("/", { state: { scrollTo: id } });
  };

  return (
    <footer data-testid="site-footer" style={{ backgroundColor: "var(--ink-raised)", borderTop: "var(--rule)" }}>
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8 py-16 grid lg:grid-cols-[1.3fr_1fr_1fr] gap-12">
        <div>
          <img src="/assets/img/logo.png" alt="Heart N Soul Records" style={{ width: 180 }} className="h-auto" />
          <p className="font-script text-gold mt-4" style={{ fontSize: "1.4rem", lineHeight: 1.1 }}>Music with Heart,<br/>Stories with Soul.</p>
          <p className="font-caps text-gold mt-4" style={{ fontSize: ".72rem" }}>Love. Faith. Purpose.</p>
          {socials.length > 0 && (
            <div className="flex flex-wrap gap-3 mt-6">
              {socials.map((s) => (
                <a key={s.name} href={socialUrls[s.name]} target="_blank" rel="noopener noreferrer" className="h-10 w-10 grid place-items-center transition-colors hover:text-gold"
                   style={{ border: "var(--rule)", borderRadius: "var(--radius)", color: "var(--parchment)" }} aria-label={s.name}>
                  <PlatformIcon name={s.name} iconName={s.icon} size={18} /><span className="sr-only">(opens in a new tab)</span>
                </a>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="font-caps text-ash mb-4" style={{ fontSize: ".58rem" }}>Explore</p>
          <ul className="space-y-3 font-caps" style={{ fontSize: ".62rem" }}>
            <li><button className="link-underline" onClick={() => anchor("music")} style={{ background: "none", border: "none", color: "var(--parchment)", cursor: "pointer" }}>Music</button></li>
            <li><button className="link-underline" onClick={() => anchor("upcoming")} style={{ background: "none", border: "none", color: "var(--parchment)", cursor: "pointer" }}>Upcoming</button></li>
            <li><button className="link-underline" onClick={() => anchor("watch")} style={{ background: "none", border: "none", color: "var(--parchment)", cursor: "pointer" }}>Watch</button></li>
            <li><Link className="link-underline" to="/merch">Merch</Link></li>
            <li><Link className="link-underline" to="/about">About</Link></li>
            <li><button className="link-underline" onClick={() => anchor("connect")} style={{ background: "none", border: "none", color: "var(--parchment)", cursor: "pointer" }}>Contact</button></li>
          </ul>
        </div>

        <div>
          <p className="font-caps text-ash mb-4" style={{ fontSize: ".58rem" }}>Legal</p>
          <ul className="space-y-3 font-caps" style={{ fontSize: ".62rem" }}>
            <li><Link className="link-underline" to="/privacy" data-testid="footer-privacy">Privacy</Link></li>
            <li><Link className="link-underline" to="/terms" data-testid="footer-terms">Terms</Link></li>
            <li><Link className="link-underline" to="/shipping-returns" data-testid="footer-shipping">Shipping &amp; Returns</Link></li>
          </ul>
        </div>
      </div>

      <div className="mx-auto max-w-[1360px] px-5 sm:px-8 pb-12">
        <div className="flex flex-wrap items-center gap-3 mb-2" style={{ color: "var(--ash)", fontSize: ".72rem" }}>
          <span>&copy; {new Date().getFullYear()} Heart N Soul Records. All rights reserved.</span>
          <Link
            to="/admin"
            aria-label="Owner sign in"
            title="Owner sign in"
            data-testid="footer-admin-link"
            className="inline-flex items-center gap-2 font-caps transition-all hover:brightness-110"
            style={{
              color: "var(--rose)",
              padding: "0.35rem 0.75rem",
              border: "1px solid var(--rose)",
              borderRadius: "var(--radius)",
              fontSize: ".58rem",
              letterSpacing: ".22em",
              fontWeight: 700,
              textDecoration: "none",
            }}
          >
            <Heart size={13} fill="var(--rose)" color="var(--rose)" /> Owner
          </Link>
        </div>
        <p style={{ color: "var(--ash)", fontSize: ".72rem", maxWidth: "60ch" }}>
          All music written, produced, and performed by Perry Frazier (P. Frazy). Unauthorized reproduction prohibited.
        </p>
      </div>
    </footer>
  );
};
